/**
 * 색상 상수 정의
 * oklch 색상 공간을 사용하여 더 넓은 색 영역과 일관된 밝기 제공
 * 
 * oklch(L% C H) 형식:
 * - L: Lightness (0-100%) - 밝기
 * - C: Chroma (0-0.4) - 채도
 * - H: Hue (0-360) - 색상
 */

import { getTenantFromDomain, TENANT_CONFIG } from '../../config/tenants.js';

// 기본 Primary 색상 Hue
const DEFAULT_PRIMARY_HUE = 206.382;

/**
 * 테넌트별 색상 생성 함수
 * @param {number} primaryHue - Primary 색상의 Hue 값 (0-360)
 * @returns {object} 색상 객체
 */
export const generateColors = (primaryHue = DEFAULT_PRIMARY_HUE) => {
  return {
    // Primary 색상 계열
    primary: `oklch(76.1% 22.75% ${primaryHue})`,
    primaryLight: `oklch(85% 15% ${primaryHue})`,
    primaryDark: `oklch(60% 25% ${primaryHue})`,
    primaryLighter: `oklch(90% 10% ${primaryHue})`,
    
    // Secondary 색상 (회색조)
    secondary: `oklch(70% 2% ${primaryHue})`,
    secondaryLight: `oklch(80% 1% ${primaryHue})`,
    secondaryDark: `oklch(60% 3% ${primaryHue})`,
    
    // Background 색상
    background: `oklch(98% 0.5% ${primaryHue})`,
    backgroundDefault: `oklch(97.5% 0.5% ${primaryHue})`,
    backgroundPaper: '#ffffff',
    
    // Text 색상
    textPrimary: `oklch(35% 5% ${primaryHue})`,
    textSecondary: `oklch(50% 3% ${primaryHue})`,
    textDisabled: `oklch(70% 1% ${primaryHue})`,
    
    // Border 색상
    border: `oklch(70% 2% ${primaryHue})`,
    borderLight: `oklch(85% 1% ${primaryHue})`,
    borderDark: `oklch(55% 3% ${primaryHue})`,
    borderFocus: `oklch(76.1% 22.75% ${primaryHue})`,
    
    // Focus 색상
    focus: `oklch(76.1% 22.75% ${primaryHue})`,
    
    // 상태 색상 (테넌트와 무관하게 고정)
    error: 'oklch(55% 25% 25)',
    warning: 'oklch(75% 20% 70)',
    success: 'oklch(65% 20% 150)',
    
    // 체크박스 색상 (Hue에 따라 약간 조정)
    checkbox: `oklch(69.83% 0.26 ${primaryHue + 40.69})`,
    
    // 로그아웃 버튼 색상
    logoutButton: 'transparent',
    logoutButtonHover: `oklch(95% 0.5% ${primaryHue})`,
    logoutButtonActive: `oklch(92% 1% ${primaryHue})`,
    
    // Login 페이지 전용 색상 (Hue에 따라 동적 생성)
    // dhsteel은 DEFAULT_PRIMARY_HUE(206.382)를 사용하므로 기본값 그대로 사용됨
    loginBackground: `oklch(0.9457 0.0128 ${primaryHue + 60.32})`,      // 연한 배경
    loginText: `oklch(0.2827 0.102 ${primaryHue + 62.03})`,           // 진한 텍스트
    loginButton: `oklch(0.7275 0.0974 ${primaryHue + 21.57})`,          // 로그인 버튼
    loginTextFieldFocus: `oklch(0.7404 0.0939 ${primaryHue + 20.73})`,  // TextField 포커스
    
    // Tabs (네비게이션 바) 전용 색상 (Hue에 따라 동적 생성)
    // DEFAULT_PRIMARY_HUE = 206.382
    tabsCompanyNameText: `oklch(0.7924 0.102 ${primaryHue - 5.502})`,        // 기업명 텍스트 색상
    tabsCompanyNameBg: `oklch(0.3698 0.0204 ${primaryHue + 39.168})`,               // 기업명 배경 색상
    tabsMenuBg: `oklch(0.4526 0.0251 ${primaryHue + 35.458})`,                            // 메뉴 배경 색상
    tabsSelectedBg: `oklch(76.1% 22.75% ${primaryHue})`,  // 선택된 메뉴 배경 (primary 색상)
    tabsSelectedBorder: `oklch(76.1% 22.75% ${primaryHue})`, // 선택된 메뉴 테두리
    tabsSelectedText: 'oklch(95% 0 0)',               // 선택된 메뉴 텍스트
    tabsMenuText: 'oklch(90% 0 0)',                  // 일반 메뉴 텍스트
    tabsMenuHoverBg: `oklch(0.414 0.0207 ${primaryHue + 36.488})`,              // 메뉴 hover 배경
    tabsMenuHoverText: 'oklch(95% 0 0)',             // 메뉴 hover 텍스트
    tabsTitleAccent: `linear-gradient(180deg, oklch(0.7596 0.0887 ${primaryHue + 1.218} 0%, oklch(0.7644 0.0968 ${primaryHue + 5.328} 100%)`, // 탭 제목 앞 그라데이션 (원본 값)
    
    // 기타
    white: '#ffffff',
    black: '#000000',
  };
};

/**
 * 현재 테넌트에 맞는 색상 가져오기
 * @returns {object} 테넌트별 색상 객체
 */
export const getTenantColors = () => {
  const tenantKey = getTenantFromDomain();
  const tenantConfig = TENANT_CONFIG[tenantKey] || TENANT_CONFIG.dhsteel;
  const primaryHue = tenantConfig.colorTheme?.primaryHue || DEFAULT_PRIMARY_HUE;
  return generateColors(primaryHue);
};

/**
 * 특정 테넌트 키로 색상 가져오기
 * @param {string} tenantKey - 테넌트 키 (예: 'dhsteel', 'oksteel')
 * @returns {object} 테넌트별 색상 객체
 */
export const getColorsByTenantKey = (tenantKey) => {
  const tenantConfig = TENANT_CONFIG[tenantKey] || TENANT_CONFIG.dhsteel;
  const primaryHue = tenantConfig.colorTheme?.primaryHue || DEFAULT_PRIMARY_HUE;
  return generateColors(primaryHue);
};

// 기본 색상 (하위 호환성을 위해 유지 - dhsteel 기본값)
export const colors = generateColors(DEFAULT_PRIMARY_HUE);

export default colors;

